import { useMemo } from 'react';

import { formatNumber } from '@/utils/candidate';
import currencies from '@/utils/currencies.json';

const DEFAULT_CURRENCY = 'EUR';
const DEFAULT_REPLACMENT_TOKEN = '¤';

export interface Currency {
  code: string;
  description: string;
  suggested: boolean;
  symbol?: string;
  locale?: string;
}

export const ALL_CURRENCIES = currencies as Currency[];
export const SUGGESTED_CURRENCIES = currencies.filter(
  ({ suggested }) => suggested,
) as Currency[];
const LOOKUP: Record<string, Currency> = Object.fromEntries(
  ALL_CURRENCIES.map((currency) => [currency.code, currency]),
);

export type CurrencyISOCode = string;

/** Values accepted by `formatNumber` / currency display helpers */
export type CurrencySubject = number | string | null | undefined;

export type CurrencyHandler = {
  /** Accepts a value which should be formatted as a currency and returns a formatted currency string */
  curr: (str: CurrencySubject) => string;
  /** Formats a number using the locale associated with the current currency (without currency symbol) */
  format: (str: CurrencySubject) => string;
  /** Changes the current currency to a new currency */
  changeCurrency: (newCurrency: CurrencyISOCode) => CurrencyHandler;
  /** Replaces a generic currency token (defaults to '¤') with the current currency */
  currReplaceToken: (str: CurrencySubject) => string;
  /** Returns the current currency */
  value: string;
};

const createFormatter = (
  currency: CurrencyISOCode,
  replacementToken = DEFAULT_REPLACMENT_TOKEN,
) => {
  const currencyData = LOOKUP[currency] || {};
  const locale = currencyData.locale || 'nl-NL'; // Default to European format
  const formatter = (str: CurrencySubject) => {
    const { symbol } = currencyData;
    const formattedValue = formatNumber(str, locale);
    return `${symbol || currency + '\xa0'}${formattedValue}`;
  };
  const tokenRegexp = new RegExp(replacementToken, 'gi');
  return {
    currReplaceToken: (str: CurrencySubject) =>
      str?.toString().replace(tokenRegexp, currency) || '',
    curr: (str: CurrencySubject) => formatter(str),
    format: (str: CurrencySubject) => formatNumber(str, locale),
    changeCurrency: (newCurrency: CurrencyISOCode) =>
      createFormatter(newCurrency || DEFAULT_CURRENCY, replacementToken),
    value: currency,
  };
};

/** Returns a set of utilities for formatting currency values */
export function createCurrencyHandler(
  currency?: string,
  replacementToken = DEFAULT_REPLACMENT_TOKEN,
): CurrencyHandler {
  return createFormatter(currency || DEFAULT_CURRENCY, replacementToken);
}

/** Returns a set of utilities for formatting currency values */
const useCurrency: (
  currency?: string,
  replacementToken?: string,
) => CurrencyHandler = (
  currency,
  replacementToken = DEFAULT_REPLACMENT_TOKEN,
) => {
  return useMemo(
    () => createCurrencyHandler(currency, replacementToken),
    [currency, replacementToken],
  );
};

export default useCurrency;
